import { axisBottom, axisLeft } from "d3-axis";
import { scaleLinear, scaleThreshold } from "d3-scale";
import { select, selectAll } from "d3-selection";
import { min, max, extent, ascending } from "d3-array";
import { geoPath, geoAlbersUsa } from "d3-geo";
import { transition } from "d3-transition";
import { easeLinear } from "d3-ease";

let topojson = require("topojson-client");
import Tooltip from './tooltipFactory.js';

export default class makeChart {

    constructor(opts) {
        Object.assign(this, opts);
        this.aspectHeight = opts.aspectHeight ? opts.aspectHeight : 0.65;
        this.legendDomain = [10,30,40,50,60,70,80,90,100,120];

        this.appendElements();
        this.update();

        this.theTooltip = new Tooltip({
            'parent' : '.chart-container',
            'formats': {
              "none" : d => d,
              "minWage" : d=> `$${d.toFixed(2)}`,
              "number" : d=> `$${formatNumber(d.toFixed(0))}`,
              "percent" : d=> `${d.toFixed(1)}%`,
              "fedCategory": d=> d >= 50 ? "Severely cost-burdened" : d > 30 ? "Cost-burdened" : "Affordable"
            }
        });
    }

    update() {
        this._setDimensions();
        this._setScales();
        this.render();
    }

    _setDimensions() {
        this.size = window.innerWidth <= 420 ? "mobile" : window.innerWidth > 421 && window.innerWidth <= 599 ? "tablet" : "desktop"; // Chris made this, handles map alignment in ways I don't want to mess with
        this.isMobile = window.innerWidth < 600 ? "mobile" : "desktop"; // Needed different breakpoints for state label positioning
    
        // define width, height and margin
        this.margin = {
          top: this.size == "desktop" ? -30 : this.size === "tablet" ? 50 : 0,
          right: 0,
          bottom: 0,
          left: 0,
        };
    
        this.width = this.element.offsetWidth - this.margin.left - this.margin.right;
        this.height = this.element.offsetWidth * this.aspectHeight - this.margin.top - this.margin.bottom;
    }

    _setScales() {
        this.projection = geoAlbersUsa()
        .scale(1)
        .translate([0, 0]);
  
        this.path = geoPath()
            .projection(this.projection);
    
        // Stuff chris wrote to center map
        let states = topojson.feature(this.stateTopo, this.stateTopo.objects.states_20m_2017);
        let b = this.path.bounds(states);
    
        let bBottom = {
            desktop: 0.23019625629687068,
            tablet: .32,
            mobile: .23
        }
    
        b[1][1] = bBottom[this.size];
    
        let sMultiply = {
            desktop: 1.05,
            tablet: 1.2,
            mobile: 1.05
        }
    
        let s = sMultiply[this.size] / Math.max((b[1][0] - b[0][0]) / this.width, (b[1][1] - b[0][1]) / this.height);
        let t = [(this.width - s * (b[1][0] + b[0][0])) / 2, (this.height - s * (b[1][1] + b[0][1])) / 2];
    
        let tOffset = {
            desktop: 40,
            tablet: 30,
            mobile: 40
        }
    
        t[0] = t[0] - (s / tOffset[this.size]); // Cheat the map to the left
    
        this.projection.scale(s).translate(t);

        this.legendScale = scaleLinear()
            .domain([10,120])
            .range(this.isMobile == "desktop" ? [0, 320] : [0,230])
        
        this.colorScale = scaleThreshold()
            .domain([30,40,50,60,70,80,90,100])
            .range(["#1ac7c2","#f7b92f","#f2844b","#cc4778","#b12b90","#8f0da4","#6a00a8","#41049d","#0d0887"]);
    
    }

    appendElements() {
        this.legend = select(".map-legend").append("svg");

        this.defs = this.legend.append("defs")

        this.affordableGradient = this.defs.append("linearGradient")
            .attr("id", "affordable-gradient")

        this.affordableStop1 = this.affordableGradient.append("stop")
            .attr("offset", 0)

        this.affordableStop2 = this.affordableGradient.append("stop")
            .attr("offset", 100)

        this.unaffordableGradient = this.defs.append("linearGradient")
            .attr("id", "unaffordable-gradient")

        this.unaffordableStop1 = this.unaffordableGradient.append("stop")
            .attr("offset", 0)

        this.unaffordableStop2 = this.unaffordableGradient.append("stop")
            .attr("offset", 100)

        // this.legendTextAffordableTop = this.legend.append("text")
        //     .text("Federal standard")
        //     .attr("class", "legend-cat-text affordable")

        this.legendTextAffordableBottom = this.legend.append("text")
            .text("Affordable")
            .attr("class", "legend-cat-text affordable-bottom")
        
        this.legendTextCostTop = this.legend.append("text")
            .text("Cost-")
            .attr("class", "legend-cat-text cost-burdened")

        this.legendTextCostBottom = this.legend.append("text")
            .text("burdened")
            .attr("class", "legend-cat-text cost-burdened")

        this.legendTextSevereTop = this.legend.append("text")
            .text("Severely")
            .attr("class", "legend-cat-text severely-cost-burdened")

        this.legendTextSevereBottom = this.legend.append("text")
            .text("cost-burdened")
            .attr("class", "legend-cat-text severely-cost-burdened")

        this.legendGroup = this.legend.selectAll(".legend-group")
          .data(this.legendDomain)
          .enter()
          .append("g")
          .attr("class", "legend-group");
    
        this.legendRect = this.legendGroup.append("rect").attr("class", "legend-rect");
        this.legendLine = this.legendGroup.append("line").attr("class", "legend-line");
        this.legendText = this.legendGroup.append("text").attr("class", "legend-text");

        this.legendTextBottom = this.legend.append("text")
            .text("of income")
            .attr("class", "legend-text")

        this.svg = select(this.element).append("svg");

        this.plot = this.svg.append("g").attr("class", "chart-g");

        this.states = this.plot.append("g")
            .attr("class", "state-g")
            .selectAll(".state")
            .data(topojson.feature(this.stateTopo, this.stateTopo.objects.states_20m_2017).features)
            .enter().append("path")
            .filter(d=> d.properties.STUSPS != "AS" && d.properties.STUSPS != "GU" && d.properties.STUSPS != "VI" && d.properties.STUSPS != "MP" && d.properties.STUSPS != "PR")
            .attr("class", d=> `${d.properties.STUSPS} state`)
            .attr("fill", "transparent");
    
        this.countyContainer = this.plot.append("g").attr("class", "county-container");

        this.countyGroup = this.countyContainer.selectAll(".county-g")
            .data(topojson.feature(this.countyTopo, this.countyTopo.objects.countyGeo).features)
            .enter()
            .filter(d=> d.properties.state != "PR")
            .append("g")
            .attr("class", d=> `c-${d.properties.fips} county`)

        this.counties = this.countyGroup.append("path")

        this.stateBorders = this.countyContainer.append("path")
            .attr("class", "state-borders")
    }

    render() {
        let t = transition()
            .duration(500)
            .ease(easeLinear)
  
        let legendWidth = this.isMobile == "desktop" ? 320 : 230;
        let legendOffset = 20;
        this.legend
          .attr("height", 50)
          .attr("width", legendWidth)
          .style("overflow", "visible");

        this.affordableStop1
          .attr("stop-color", "#ffffff")

        this.affordableStop2
          .attr("stop-color", this.colorScale.range()[0])

        this.unaffordableStop1
            .attr("stop-color", this.colorScale.range()[this.colorScale.range().length-1])

        this.unaffordableStop2
            .attr("stop-color", "#ffffff")

        // this.legendTextAffordableTop
        //     .attr("x", this.legendScale(29))
        //     .attr("y", -2)
        //     .attr("fill", "#1abab2")
        //     .attr("text-anchor", "end")
        this.legendTextAffordableBottom
            .attr("x", this.legendScale(29))
            .attr("y", 13)
            .attr("fill", "#1abab2")
            .attr("text-anchor", "end")
        this.legendTextCostTop
            .attr("x", this.legendScale(31))
            .attr("y", -2)
            .attr("fill", this.colorScale(49))
            .attr("text-anchor", "start")
        this.legendTextCostBottom
            .attr("x", this.legendScale(31))
            .attr("y", 13)
            .attr("fill", this.colorScale(49))
            .attr("text-anchor", "start")
        this.legendTextSevereTop
            .attr("x", this.legendScale(120))
            .attr("y", -2)
            .attr("fill", this.colorScale(100))
            .attr("text-anchor", "end")
        this.legendTextSevereBottom
            .attr("x", this.legendScale(120))
            .attr("y", 13)
            .attr("fill", this.colorScale(100))
            .attr("text-anchor", "end")
    
        this.legendGroup
          .attr("transform", (d,i) => `translate(${this.legendScale(d)}, 0)`)
    
        this.legendRect
          .attr("height", 8)
          .attr("width", (d,i) => i == this.legendDomain.length-1 ? this.legendScale(this.legendDomain[i]) - this.legendScale(d) : this.legendScale(this.legendDomain[i+1]) - this.legendScale(d))
          .attr("y", legendOffset)
          .attr("x", 0)
          .transition(t)
          .attr("fill", (d,i) => {
              if (i == 0) {
                  return "url(#affordable-gradient)"
              } else if (i == this.colorScale.domain().length) {
                return "url(#unaffordable-gradient)"
              } else {
                return this.colorScale(d)
              }
            })
    
        this.legendLine
          .attr("y1", legendOffset)
          .attr("y2", legendOffset+16)
    
        this.legendText
          .text((d,i)=> i == this.legendDomain.length-2 ? `${parseInt(d)}%` : parseInt(d))
          .attr("y", this.isMobile == "desktop" ? legendOffset+28 : legendOffset+28)
          .attr("text-anchor", (d,i) => i == this.legendDomain.length-2 ? "start" : "middle")
          .filter((d,i) => i == this.legendDomain.length-2)
            .attr("text-anchor", "start")
            .attr("dx", -5)

        this.legendTextBottom
            .attr("y", this.isMobile == "mobile" ? legendOffset+40 : legendOffset+43)
            .attr("x", this.legendScale(this.legendDomain[this.legendDomain.length-2]))
            .attr("dx", -5);

        this.svg.attr("width", this.width + this.margin.left + this.margin.right);
        this.svg.attr("height", this.height + this.margin.top + this.margin.bottom);

        this.plot.attr("transform", `translate(${this.margin.left},${this.margin.top})`);

        this.states
            .attr("d", this.path)

        this.stateBorders
            .attr("d", this.path(topojson.mesh(this.stateTopo, this.stateTopo.objects.states_20m_2017, function (a, b) {
                return a !== b;
            })));

        this.counties
            .attr("d", this.path)
            .on('mouseover', (event,d) =>{
                let countyClass = `c-${d.properties.fips}`;
                select(`.county.${countyClass}`).classed('is-active',true).raise();
                select(".chart-container").classed('tooltip-active',true);
        
                let bbox = document.querySelector(`.county.${countyClass}`).getBoundingClientRect();
        
                let coords = [
                  bbox.x + bbox.width - bbox.width/2,
                  bbox.y + bbox.height - bbox.height/2
                ];

                let thisCountiesRate = d.properties[this.metric];

                let pillColor = this.colorScale(thisCountiesRate);

                select(".tt-update.number.rentShare")
                    .style("background-color", pillColor)
                    .style("color", thisCountiesRate >=50 ? "rgba(255,255,255,0.95)" : "#121212")

                let w = this.width + this.margin.left + this.margin.right;
                let h = this.height + this.margin.top + this.margin.bottom;
                this.theTooltip.position(d.properties,coords,[w,h])
        
                let ttNumberText = document.querySelectorAll(".tt-update.number"); // To handle blank entries in tooltips
                ttNumberText.forEach((d,i)=> {
                  if (d.innerHTML == "") {
                    return d.innerHTML = "0"
                  }
                })

                if (d.properties.rentShare == "") {
                    select(".tt-update.fedCategory")
                        .style("display", "none")
                    select(".tt-update.rent2br")
                        .text("N/A")
                        .classed("no-data", true)
                    select(".tt-update.rentShare")
                        .text("N/A")
                        .classed("no-data", true)
                }

                if (!d.properties.rentShare) {
                    select(".tt-update.fedCategory")
                        .style("display", "none")
                    select(".tt-update.minWage")
                        .text("N/A")
                        .classed("no-data", true)
                    select(".tt-update.rentShare")
                        .text("N/A")
                        .classed("no-data", true)
                }

              })
              .on('mouseleave', (d,i,e) =>{
                selectAll(".county").classed("is-active", false);
                select(".state-borders").raise();
                select(".chart-container").classed('tooltip-active',false);
                selectAll(".tt-row").style("display", "block");
                select(".tt-update.rent2br").classed("no-data", false);
                select(".tt-update.rentShare").classed("no-data", false);
                select(".tt-update.minWage").classed("no-data", false);
                select(".tt-update.fedCategory").style("display", "block");
                this.theTooltip.deposition()
              })
            .transition(t)
            .attr("fill", d=> d.properties[this.metric] ? this.colorScale(d.properties[this.metric]) : "#ececec");

        select(".headline")
            .html(window.innerWidth <= 410 ? "Percentage of income <span class='hed-underline'>the</span></br><span class='hed-underline'>average renter</span> needs to spend</br>on a two-bedroom rental" : "Percentage of income <span class='hed-underline'>the average renter</span></br>needs to spend on a two-bedroom rental");

        select(".headline-units")
            .html(window.innerWidth <= 520 ? "The average renter’s wage varies by county. Federal housing programs use 30% of income as a threshold for whether housing is affordable."
            : "The average renter’s wage varies by county. Federal housing programs use</br>30% of income as a threshold for whether housing is affordable.");

    }

}

function spike(length, width = 7) {
    return `M${-width / 2},0L0,${-length}L${width / 2},0`
}

function slugify(text) {
    return text.toString().toLowerCase()
    .replace(/\s+/g, '-')           // Replace spaces with -
    .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
    .replace(/\-\-+/g, '-')         // Replace multiple - with single -
    .replace(/^-+/, '')             // Trim - from start of text
    .replace(/-+$/, '');            // Trim - from end of text
}

function formatNumber(x) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}